import { useCallback, useMemo } from 'react';
import { atom, useAtomValue } from 'jotai';
import { useAsync } from 'react-async-hook';
import { useQuery } from 'react-query';

import DistrictsAPI from 'services/API/DistrictsAPI';
import ProjectsAPI from 'services/API/ProjectsAPI';
import { currentCityAtom } from 'state/currentLocation';
import { Item, ParentItem } from 'types';
import { cityMap } from 'utils/misc';

export type LudState = {
  id: number;
  code: string;
  district: string;
  description: string;
};

type VerticalRaw = {
  id: number;
  name: string;
  sector: { id: number; name: string };
};

// Atoms
const ludsAtom = atom(async () => {
  const { data } = await DistrictsAPI.getAllDistrictCodes();

  return data.map<LudState>(({ id, lud_code, lud_description, lud_district }) => ({
    id,
    code: lud_code,
    description: lud_description,
    district: lud_district,
  }));
});
ludsAtom.debugLabel = 'landUseDesignationCodes';

const ludsAtomByCity = atom(async (get) => {
  const cityName = await get(currentCityAtom);
  const cityId = cityMap[cityName];

  if (!cityId || cityId === undefined) {
    return [];
  }

  const { data } = await DistrictsAPI.getDistrictCodesByCity(cityId);

  return data.map<LudState>(({ id, lud_code, lud_description, lud_district }) => ({
    id,
    code: lud_code,
    description: lud_description,
    district: lud_district,
  }));
});
ludsAtom.debugLabel = 'landUseDesignationCodesByCity';

// Custom hooks for fetching data using react-query
const useFetchSectors = () => {
  return useQuery(
    ['sectors'],
    async (): Promise<Item[]> => {
      const result = await ProjectsAPI.getSectors();

      return (result?.data as Item[]) || [];
    },
    {
      // Arbitrary cache time of 6 hours - this data is unlikely to change often
      cacheTime: 1000 * 60 * 60 * 6,
      refetchOnWindowFocus: false,
    },
  );
};

const useFetchVerticals = () => {
  return useQuery(
    ['verticals'],
    async (): Promise<VerticalRaw[]> => {
      const result = await ProjectsAPI.getVerticals();

      return (result?.data as VerticalRaw[]) || [];
    },
    {
      // Arbitrary cache time of 6 hours - this data is unlikely to change often
      cacheTime: 1000 * 60 * 60 * 6,
      refetchOnWindowFocus: false,
    },
  );
};

const ludsByGroupCity = async (cityId: string | undefined) => {
  if (!cityId || cityId === undefined || cityId === 'undefined') {
    return [];
  }

  const { data } = await DistrictsAPI.getDistrictCodesByCity(cityId);

  return data.map<LudState>(({ id, lud_code, lud_description, lud_district }) => ({
    id,
    code: lud_code,
    description: lud_description,
    district: lud_district,
  }));
};

// Hooks
export const useZones = (cityOverride?: string | undefined) => {
  const data = useAtomValue(ludsAtom);
  const dataByGroupCity = useAsync(ludsByGroupCity, [cityOverride]);
  const dataByCurrentCity = useAtomValue(ludsAtomByCity);
  const dataByCity = useMemo(() => {
    return (cityOverride ? dataByGroupCity.result : dataByCurrentCity) || [];
  }, [dataByCurrentCity, dataByGroupCity.result, cityOverride]);

  const zones = useMemo(
    () => Object.keys(data.reduce((o, { district }) => ({ ...o, [district]: '' }), {})),
    [data],
  );

  const getLuds = useCallback(
    (zone: string) => data.filter(({ district }) => district === zone),
    [data],
  );

  const getLudsByCurrentCity = useCallback(
    (zone: string) => dataByCurrentCity.filter(({ district }) => district === zone),
    [dataByCurrentCity],
  );

  const getZonesFromLuds = useCallback(
    (...ids: number[]) =>
      data
        .filter(({ id }) => ids.includes(id))
        .reduce<Record<string, number>>(
          (obj, { district }) => ({
            ...obj,
            [district]: (obj[district] ?? 0) + 1,
          }),
          {},
        ),
    [data],
  );

  const getZonesFromLudsDetails = useCallback(
    (...ids: number[]) => {
      const temp = data
        .filter(({ id }) => ids.includes(id))
        .reduce<Record<string, LudState[]>>(
          (obj, lud) => ({
            ...obj,
            [lud.district]: [...(obj[lud.district] ?? []), lud],
          }),
          {},
        );

      return Object.entries(temp).map<ParentItem>(([district, luds], id) => ({
        id,
        name: district,
        children: luds.map((lud) => ({
          id: lud.id,
          name: lud.code,
          description: lud.description,
        })),
      }));
    },
    [data],
  );

  const getAllCityLuds = useCallback(() => {
    return dataByCity;
  }, [dataByCity]);

  const getZonesFromCodes = useCallback(
    (codes: string[]) => {
      return [
        ...dataByCity.filter(({ code }) => codes.includes(code)).map(({ id }) => id),
      ];
    },
    [dataByCity],
  );

  const getZonesForCity = useMemo(() => {
    const zonesFromLudsDetails = getZonesFromLudsDetails(
      ...dataByCity.map(({ id }) => id),
    );

    return zonesFromLudsDetails;
  }, [dataByCity, getZonesFromLudsDetails]);

  return {
    zones,
    getLuds,
    getZonesFromLuds,
    getZonesFromLudsDetails,
    getZonesForCity,
    getAllCityLuds,
    getZonesFromCodes,
    getLudsByCurrentCity,
  };
};

export const useSectors = () => {
  const {
    data: sectors = [], // Default to an empty array if data is undefined
    isLoading: sectorsLoading,
  } = useFetchSectors();

  const {
    data: verticals = [], // Default to an empty array if data is undefined
    isLoading: verticalsLoading,
  } = useFetchVerticals();

  const getVerticalsForSector = useCallback(
    (sectorId: number) => {
      if (!verticals) {
        return [];
      }

      return verticals.filter(({ sector }) => sector.id === sectorId);
    },
    [verticals],
  );

  const getSectorForVertical = useCallback(
    (id: number) => {
      const sectorId = verticals?.find((v) => v.id === id)?.sector.id;

      return sectors.find((s) => s.id === sectorId);
    },
    [sectors, verticals],
  );

  return {
    sectors,
    verticals,
    getVerticalsForSector,
    getSectorForVertical,
    sectorsLoading,
    verticalsLoading,
  };
};
